// 採点のカテゴリの重み(品目ごと)。
//
// 既定は CLI の CATEGORY_WEIGHTS(src/core/compare.ts)と同じ。Web は CLI のコードを
// 読み込まないので値を持ち直している。食い違いは tests/score-weights-sync.test.ts が見る。
// 検証は Functions(functions/score-weights-lib.mjs)と同じ決まり: 0〜100 の整数・合計100。
import { CATEGORY_LABEL, CATEGORY_ORDER, type Category } from "./evaluation";

export type ScoreWeights = Record<Category, number>;

/** 既定の重み(百分率) */
export const DEFAULT_SCORE_WEIGHTS: ScoreWeights = {
  critical_point: 45,
  ingredient: 35,
  heat_level: 15,
  plating: 5,
};

/** 並び(評価結果の画面と同じ) */
export const WEIGHT_ORDER: Category[] = CATEGORY_ORDER;

/** 品目の重み。設定が無ければ既定 */
export function weightsOf(dish: { scoreWeights?: Partial<ScoreWeights> | null }): ScoreWeights {
  const w = dish.scoreWeights;
  if (!w) return { ...DEFAULT_SCORE_WEIGHTS };
  const out = { ...DEFAULT_SCORE_WEIGHTS };
  for (const c of WEIGHT_ORDER) {
    const v = w[c];
    if (typeof v === "number" && Number.isFinite(v)) out[c] = v;
  }
  return out;
}

export function isDefaultWeights(w: ScoreWeights): boolean {
  return WEIGHT_ORDER.every((c) => w[c] === DEFAULT_SCORE_WEIGHTS[c]);
}

export type WeightsCheck =
  | { ok: true; weights: ScoreWeights; total: number }
  | { ok: false; error: string; total: number | null; params?: Record<string, number> };

/**
 * 入力欄の文字列を検証する。エラー文言は t() に通す前の日本語(params で数を埋める)。
 */
export function checkWeights(draft: Record<Category, string>): WeightsCheck {
  const out = {} as ScoreWeights;
  for (const c of WEIGHT_ORDER) {
    const s = (draft[c] ?? "").trim();
    if (!/^\d{1,3}$/.test(s) || Number(s) > 100) {
      return { ok: false, error: "重みは 0〜100 の整数で入力してください", total: null };
    }
    out[c] = Number(s);
  }
  const total = WEIGHT_ORDER.reduce((n, c) => n + out[c], 0);
  if (total !== 100) {
    return { ok: false, error: "重みの合計を100にしてください(いまは{n})", total, params: { n: total } };
  }
  return { ok: true, weights: out, total };
}

/** 「重要ポイント 45% / 食材 35% / …」。ラベルは翻訳関数に通す */
export function formatWeights(w: ScoreWeights | null | undefined, t: (s: string) => string): string {
  const v = w ?? DEFAULT_SCORE_WEIGHTS;
  return WEIGHT_ORDER.map((c) => `${t(CATEGORY_LABEL[c])} ${v[c]}%`).join(" / ");
}
